import * as amplitude from "@amplitude/analytics-browser";
import {
  sessionReplayPlugin,
  type SessionReplayPlugin,
} from "@amplitude/plugin-session-replay-browser";
import { memo, type ReactNode, useEffect } from "react";

const sessionReplayTrackingByProduct = new Map<string, SessionReplayPlugin>();
const sessionReplayAddPromiseByProduct = new Map<string, Promise<unknown>>();

function canTrackSessionReplay(): boolean {
  return (
    typeof window !== "undefined" &&
    process.env.NODE_ENV !== "development" &&
    globalThis.REDO_MOCK_MODE !== "true" &&
    !!globalThis.AMPLITUDE_API_KEY
  );
}

function getSessionReplayTracking(
  productId: string,
  blockSelector?: string[],
): SessionReplayPlugin {
  const existing = sessionReplayTrackingByProduct.get(productId);
  if (existing) {
    return existing;
  }

  const sessionReplayTracking = sessionReplayPlugin({
    forceSessionTracking: true,
    privacyConfig: {
      defaultMaskLevel: "medium",
      maskSelector: [".amp-mask"],
      ...(blockSelector ? { blockSelector } : {}),
    },
    sampleRate: 1,
  });
  sessionReplayTrackingByProduct.set(productId, sessionReplayTracking);
  return sessionReplayTracking;
}

function enableSessionReplay(productId: string, blockSelector?: string[]) {
  const sessionReplayTracking = getSessionReplayTracking(
    productId,
    blockSelector,
  );
  sessionReplayAddPromiseByProduct.set(
    productId,
    amplitude.add(sessionReplayTracking).promise.catch(() => undefined),
  );
}

function disableSessionReplay(productId: string) {
  const sessionReplayTracking = sessionReplayTrackingByProduct.get(productId);
  if (!sessionReplayTracking) {
    return;
  }

  void (sessionReplayAddPromiseByProduct.get(productId) ?? Promise.resolve())
    .then(() => amplitude.remove(sessionReplayTracking.name).promise)
    .catch(() => undefined)
    .finally(() => {
      sessionReplayAddPromiseByProduct.delete(productId);
      sessionReplayTrackingByProduct.delete(productId);
    });
}

export const AmplitudeSessionReplayProvider = memo(
  function AmplitudeSessionReplayProvider({
    blockSelector,
    children,
    enabled = true,
    productId,
  }: {
    blockSelector?: string[];
    children: ReactNode;
    enabled?: boolean;
    productId: string;
  }) {
    useEffect(() => {
      if (!enabled || !canTrackSessionReplay()) {
        return;
      }
      enableSessionReplay(productId, blockSelector);
      return () => disableSessionReplay(productId);
    }, [blockSelector, enabled, productId]);

    return <>{children}</>;
  },
);
